import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Dialog, DialogContent, DialogTitle, Chip, Stack, CircularProgress, IconButton } from '@mui/material';
import { Close, ZoomIn, PictureAsPdf, Description, Download } from '@mui/icons-material';
import { renderAsync } from 'docx-preview';
import { getAdmFileUrl } from '../../utils/admProgress';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import SecureFileImg from '../SecureFileImg';
import SecureFilePdf from '../SecureFilePdf';
import { fetchAuthedBlobUrl, toProtectedFileUrl } from '../../utils/secureFile';

const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';

function getFileUrl(file, fileBase) {
  if (!file) return '';
  if (String(file).startsWith('http') || String(file).startsWith('blob:') || String(file).startsWith('data:')) {
    return toProtectedFileUrl(file);
  }
  if (String(file).startsWith('BA_Verifikasi_') || String(file).startsWith('BA_Signed_')) {
    return `${BASE_URL}/ba/${file}`;
  }
  // For RAB progress files (financial/physical/admin), construct URL based on fileBase
  if (fileBase === 'rab-progress') {
    const filename = String(file).split('/').pop();
    return `${BASE_URL}/files/rab-progress/${filename}`;
  }
  if (fileBase === 'rab-progress-fisik') {
    const filename = String(file).split('/').pop();
    return `${BASE_URL}/files/rab-progress-fisik/${filename}`;
  }
  // Fallback for adm-only mode
  return getAdmFileUrl(file, fileBase || null, BASE_URL);
}

export function FilePreviewModal({ open, onClose, files = [], labels = [], fileBase, title = 'Preview File', activeIndex = 0 }) {
  const [activeIdx, setActiveIdx] = useState(activeIndex || 0);
  const [docxLoading, setDocxLoading] = useState(false);
  const [docxReady, setDocxReady] = useState(false);
  const [docxError, setDocxError] = useState('');
  const [docxBlobUrl, setDocxBlobUrl] = useState('');
  const docxContainerRef = useRef(null);

  const safeFiles = Array.isArray(files) ? files : [];
  const safeLabels = Array.isArray(labels) ? labels : [];

  const currentFile = safeFiles[activeIdx];
  const currentLabel = safeLabels[activeIdx] || currentFile || '';
  const url = getFileUrl(currentFile, fileBase);
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(String(currentFile));
  const isPdf = /\.pdf$/i.test(String(currentFile));
  const isDocx = /\.(docx|doc)$/i.test(String(currentFile));

  useEffect(() => {
    setActiveIdx(activeIndex || 0);
  }, [activeIndex, open]);

  useEffect(() => {
    let active = true;
    setDocxLoading(false);
    setDocxReady(false);
    setDocxError('');
    setDocxBlobUrl('');
    if (!open || !isDocx) return undefined;

    setDocxLoading(true);
    fetchAuthedBlobUrl(url)
      .then((blobUrl) => {
        if (!active) return;
        setDocxBlobUrl(blobUrl);
      })
      .catch(() => {
        if (active) setDocxError('Gagal memuat file Word untuk preview.');
      })
      .finally(() => {
        if (active) setDocxLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, isDocx, url]);

  useEffect(() => {
    let active = true;
    if (!open || !isDocx || !docxBlobUrl || !docxContainerRef.current) {
      return undefined;
    }

    setDocxLoading(true);
    fetch(docxBlobUrl)
      .then((response) => response.blob())
      .then(async (blob) => {
        if (!active || !docxContainerRef.current) return;
        docxContainerRef.current.innerHTML = '';
        await renderAsync(blob, docxContainerRef.current, undefined, {
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: true,
          breakPages: true,
          useBase64URL: true,
        });
        if (active) setDocxReady(true);
      })
      .catch(() => {
        if (active) setDocxError('Preview dokumen tidak tersedia di browser.');
      })
      .finally(() => {
        if (active) setDocxLoading(false);
      });

    return () => {
      active = false;
    };
  }, [docxBlobUrl, isDocx, open]);

  if (!safeFiles.length) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" noWrap sx={{ maxWidth: '80%' }}>{title} — {currentLabel}</Typography>
        <Button size="small" onClick={onClose}><Close fontSize="small" /></Button>
      </DialogTitle>
      <DialogContent>
        {isImage ? (
          <Box sx={{ textAlign: 'center' }}>
            <SecureFileImg src={url} alt={currentLabel} enablePreview={false} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          </Box>
        ) : isPdf ? (
          <SecureFilePdf src={url} title={currentLabel} height={500} />
        ) : isDocx ? (
          <Box>
            {docxLoading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={28} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">Menyiapkan preview dokumen...</Typography>
              </Box>
            )}
            <Box
              ref={docxContainerRef}
              data-testid="docx-preview-root"
              sx={{
                display: docxReady ? 'block' : 'none',
                maxHeight: '65vh',
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                bgcolor: '#f5f5f5',
                '& .docx-wrapper': { background: '#f5f5f5', padding: '12px' },
                '& .docx-wrapper section.docx': { boxShadow: '0 0 6px rgba(0,0,0,0.2)', marginBottom: '12px' },
              }}
            />
            {!docxLoading && !docxReady && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Description sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>{currentLabel}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {docxError || 'Preview dokumen ini belum tersedia di browser. Silakan unduh file untuk membuka.'}
                </Typography>
                {url && (
                  <Button variant="contained" startIcon={<Download />} onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                    Unduh Dokumen
                  </Button>
                )}
              </Box>
            )}
            {docxReady && (
              <Stack direction="row" justifyContent="center" sx={{ mt: 1.5 }}>
                <Button variant="outlined" startIcon={<Download />} onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                  Unduh Dokumen Asli
                </Button>
              </Stack>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1">{currentFile}</Typography>
          </Box>
        )}

        {safeFiles.length > 1 && (
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2, flexWrap: 'wrap' }}>
            {safeFiles.map((f, i) => (
              <Chip key={i} label={safeLabels[i] || `#${i + 1}`} size="small"
                variant={i === activeIdx ? 'filled' : 'outlined'} color={i === activeIdx ? 'primary' : 'default'}
                onClick={() => setActiveIdx(i)} sx={{ cursor: 'pointer' }} />
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function FileThumbnail({ file, label, fileBase, size = 56, onPreview }) {
  if (!file) return null;
  const url = getFileUrl(file, fileBase);
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(String(file));
  const isPdf = /\.pdf$/i.test(String(file));
  const isDocx = /\.(docx|doc)$/i.test(String(file));

  const handlePreview = () => {
    if (onPreview) return onPreview();
    openInAppFilePreview({ url, labels: [label || file], title: label || 'Preview' });
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {isImage ? (
        <SecureFileImg
          src={url}
          alt={label || file}
          enablePreview={false}
          onClick={handlePreview}
          style={{ width: size, height: size, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd', cursor: 'pointer' }}
        />
      ) : (
        <Chip icon={isPdf ? <PictureAsPdf /> : isDocx ? <Description /> : <Description />} label={label || file} size="small" variant="outlined"
          color={isDocx ? 'primary' : 'default'}
          onClick={handlePreview} sx={{ cursor: 'pointer', maxWidth: 200 }} />
      )}
      <IconButton size="small" sx={{ p: 0.2 }} onClick={handlePreview}>
        <ZoomIn fontSize="small" sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}
